import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Grid,
  LinearProgress,
  Alert,
} from '@mui/material'
import {
  PhotoCamera as CameraIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material'
import { ChecklistCategory } from '@types'

interface ImageUploaderProps {
  category: ChecklistCategory
  value?: string[]
  onChange?: (images: string[]) => void
  disabled?: boolean
  maxImages?: number
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  category,
  value = [],
  onChange,
  disabled = false,
  maxImages = 5,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (disabled) return

    setError(null)
    setUploading(true)
    setUploadProgress(0)

    try {
      const newImages: string[] = []
      
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i]
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError(`File ${file.name} is too large. Maximum size is 5MB.`)
          continue
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
          setError(`File ${file.name} is not an image.`)
          continue
        }

        // Create preview URL
        const imageUrl = URL.createObjectURL(file)
        newImages.push(imageUrl)
        
        // Update progress
        setUploadProgress(((i + 1) / acceptedFiles.length) * 100)
      }

      // Limit total images
      const totalImages = [...value, ...newImages]
      const limitedImages = totalImages.slice(0, maxImages)
      
      onChange?.(limitedImages)
    } catch (err: any) {
      setError(err.message || 'Failed to upload images')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }, [value, onChange, disabled, maxImages])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true,
    disabled: disabled || uploading,
  })

  const handleRemoveImage = (index: number) => {
    if (disabled) return
    
    const newImages = value.filter((_, i) => i !== index)
    onChange?.(newImages)
  }

  const handleCameraCapture = () => {
    // This would typically open camera on mobile devices
    // For now, we'll trigger the file input
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment' // Use rear camera
    input.multiple = true
    
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      onDrop(files)
    }
    
    input.click()
  }

  return (
    <Box>
      {/* Upload Area */}
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          border: isDragActive ? '2px dashed' : '1px solid',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <CardContent>
          <Box
            {...getRootProps()}
            sx={{
              textAlign: 'center',
              py: 3,
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input {...getInputProps()} />
            
            <CloudUploadIcon 
              sx={{ 
                fontSize: 48, 
                color: 'text.secondary', 
                mb: 1 
              }} 
            />
            
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop images here' : 'Upload Images'}
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Drag and drop images here, or click to select files
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={disabled || uploading}
              >
                Choose Files
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={handleCameraCapture}
                disabled={disabled || uploading}
              >
                Camera
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Upload Progress */}
      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Uploading images... {Math.round(uploadProgress)}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Image Preview Grid */}
      {value.length > 0 && (
        <Grid container spacing={2}>
          {value.map((imageUrl, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card>
                <CardMedia
                  component="img"
                  height="120"
                  image={imageUrl}
                  alt={`Inspection image ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      Image {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveImage(index)}
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Image Count Info */}
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        {value.length} of {maxImages} images uploaded
      </Typography>
    </Box>
  )
}

export default ImageUploader
